import Layout from '@components/layout'
import { passwordRecoveryPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityPasswordRecoveryPage } from '@data/sanity/queries/types/page'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import {
  defaultModuleQueryParameters,
  getPasswordRecoveryPage,
} from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type PasswordRecoveryPageProps = BasePageProps & {
  page: SanityPasswordRecoveryPage
  site: SanitySiteFragment
}

const PasswordRecoveryPage = ({
  draftMode,
  locale,
  page,
  site,
}: PasswordRecoveryPageProps) => {
  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout page={page} site={site} schemas={schemas} draftMode={draftMode}>
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={passwordRecoveryPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<PasswordRecoveryPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const passwordRecoveryPage = await getPasswordRecoveryPage(
    sanityClient,
    context.locale,
  )

  if (!passwordRecoveryPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      page: getNonNullValues(passwordRecoveryPage.page),
      site: getNonNullValues(passwordRecoveryPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export default PasswordRecoveryPage
